import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null); // Lightbox state
  const [tocOpen, setTocOpen] = useState(true);

  // Helper to slugify heading text for smooth anchor links
  const slugify = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  };

  // Process HTML to inject IDs into h2 tags and extract table of contents items
  const processedContent = React.useMemo(() => {
    if (!service?.beskrivelse?.da) return { html: '', toc: [] };

    const rawHtml = service.beskrivelse.da;
    const toc = [];
    let headingIndex = 0;

    const htmlWithIds = rawHtml.replace(/<h2>(.*?)<\/h2>/gi, (match, title) => {
      // Strip any nested tags inside h2 if any
      const cleanTitle = title.replace(/<[^>]*>?/gm, '').trim();
      const id = `afsnit-${headingIndex}-${slugify(cleanTitle)}`;
      headingIndex++;
      toc.push({ id, title: cleanTitle });
      return `<h2 id="${id}">${title}</h2>`;
    });

    return { html: htmlWithIds, toc };
  }, [service]);

  useEffect(() => {
    const fetchServiceData = async () => {
      setLoading(true);
      try {
        // 1. Fetch service by slug
        const serviceRes = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`);
        if (!serviceRes.ok) {
          if (serviceRes.status === 404) {
            navigate('/404');
          } else {
            navigate('/403');
          }
          return;
        }
        const serviceData = await serviceRes.json();
        setService(serviceData);

        // 2. Fetch associated gallery if reference exists
        if (serviceData.galleri_reference) {
          const galleryRes = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${serviceData.galleri_reference}`);
          if (galleryRes.ok) {
            const galleryData = await galleryRes.json();
            setGallery(galleryData);
          }
        }

        // 3. Fetch related projects
        const projectsRes = await fetch(`https://api.dev.auxo.dk/public/v2/content/projekter?tilknyttet_ydelse=${slug}`);
        if (projectsRes.ok) {
          const projectsData = await projectsRes.json();
          setRelatedProjects(projectsData.items || []);
        }
      } catch (err) {
        console.error('Error fetching service detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-secondary" />
          <p className="text-slate-500 font-medium">Henter oplysninger...</p>
        </div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <>
      <Helmet>
        <title>{service.meta_title?.da || `${service.titel?.da || service.titel} | Tømrerfirmaet RNS Djursland`}</title>
        <meta name="description" content={service.meta_description?.da || `Læs mere om vores arbejde med ${service.titel?.da?.toLowerCase() || 'tømrerarbejde'} på Djursland, i Grenaa, Ebeltoft og omegn. Kontakt Tømrerfirmaet RNS.`} />
        <meta property="og:title" content={service.meta_title?.da || `${service.titel?.da || service.titel} | Tømrerfirmaet RNS`} />
        <meta property="og:description" content={service.meta_description?.da || `Læs mere om vores arbejde med ${service.titel?.da?.toLowerCase() || 'tømrerarbejde'} på Djursland, i Grenaa, Ebeltoft og omegn.`} />
        {service.hero_billede && service.hero_billede[0] && (
          <meta property="og:image" content={service.hero_billede[0]} />
        )}
        <link rel="canonical" href={`https://rns.dk/ydelser/${slug}`} />
        <html lang="da-DK" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 text-white">
        <div className="absolute inset-0 z-0">
          <img
            src={
              service.hero_billede && service.hero_billede[0]
                ? `https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${service.hero_billede[0].replace('https://assets.auxodev.dk/', '')}`
                : 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/carpenter-wood-boards.jpg'
            }
            alt={`${service.titel?.da || service.titel} på Djursland - Tømrerfirmaet RNS`}
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center space-y-6">
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl">
            {service.titel.da}
          </h1>
          
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="bg-brand-primary hover:bg-brand-primary/90 text-white font-bold py-2.5 px-5 text-sm sm:py-3 sm:px-8 sm:text-base rounded-none transition-all shadow-lg inline-flex items-center gap-2"
            >
              <i className="fa-solid fa-envelope" />
              Få et uforpligtende tilbud
            </Link>
            <a
              href="tel:22439147"
              className="bg-transparent text-white font-bold py-2.5 px-5 text-sm sm:py-3 sm:px-8 sm:text-base rounded-none transition-all border border-white inline-flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-white" />
              +45 22 43 91 47
            </a>
          </div>
        </div>
      </section>

      {/* Main Content & Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Text Content */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-6">
              {/* Feature info boxes */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 mb-2">
                <div className="bg-slate-50 border border-slate-200/80 p-4 flex flex-col justify-between shadow-2xs hover:border-brand-primary/40 transition-colors">
                  <div className="w-9 h-9 bg-brand-primary/10 text-brand-primary flex items-center justify-center mb-3">
                    <i className="fa-solid fa-shield-check text-base" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-slate-900 text-sm mb-1">
                      Kvalitet & Sikkerhed
                    </h3>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      Korrekt udførsel efter gældende lovkrav og sikkerhed.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200/80 p-4 flex flex-col justify-between shadow-2xs hover:border-brand-primary/40 transition-colors">
                  <div className="w-9 h-9 bg-brand-primary/10 text-brand-primary flex items-center justify-center mb-3">
                    <i className="fa-solid fa-map-location-dot text-base" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-slate-900 text-sm mb-1">
                      Lokal på Djursland
                    </h3>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      Ebeltoft, Grenaa, Tirstrup og resten af Djursland.
                    </p>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200/80 p-4 flex flex-col justify-between shadow-2xs hover:border-brand-primary/40 transition-colors">
                  <div className="w-9 h-9 bg-brand-primary/10 text-brand-primary flex items-center justify-center mb-3">
                    <i className="fa-solid fa-calculator text-base" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-slate-900 text-sm mb-1">
                      Uforpligtende tilbud
                    </h3>
                    <p className="text-slate-600 text-xs leading-relaxed">
                      Grundig vurdering af opgaven og rådgivning.
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Table of Contents / Navigation for long SEO guides */}
              {processedContent.toc.length > 1 && (
                <div className="bg-slate-50 border border-slate-200 p-5 sm:p-6 transition-all shadow-2xs my-6">
                  <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 pb-3 mb-3.5">
                    <div className="flex items-center gap-2.5">
                      <span className="w-7 h-7 bg-brand-primary text-white flex items-center justify-center text-xs font-bold">
                        <i className="fa-solid fa-list-check text-xs" />
                      </span>
                      <h3 className="font-display font-bold text-slate-900 text-sm sm:text-base">
                        Hurtigt overblik over guiden
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => setTocOpen(!tocOpen)}
                      className="text-xs font-semibold text-slate-600 hover:text-brand-primary flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-slate-200/60 transition-colors"
                      aria-expanded={tocOpen}
                    >
                      <span>{tocOpen ? 'Skjul' : 'Vis'}</span>
                      <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${tocOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {tocOpen && (
                    <nav aria-label="Indholdsfortegnelse">
                      <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                        {processedContent.toc.map((item, idx) => (
                          <li key={item.id}>
                            <a
                              href={`#${item.id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                const element = document.getElementById(item.id);
                                if (element) {
                                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }}
                              className="group flex items-start gap-2 p-2 bg-white hover:bg-brand-primary/5 border border-slate-200/70 hover:border-brand-primary/40 transition-all font-medium text-slate-700 hover:text-brand-primary leading-snug"
                            >
                              <span className="shrink-0 w-5 h-5 bg-slate-100 group-hover:bg-brand-primary group-hover:text-white text-slate-500 text-[11px] font-bold flex items-center justify-center transition-colors">
                                {idx + 1}
                              </span>
                              <span className="line-clamp-2">{item.title}</span>
                            </a>
                          </li>
                        ))}
                      </ol>
                    </nav>
                  )}
                </div>
              )}

              {/* Main Service Description formatted creatively */}
              <div
                className="service-prose max-w-none"
                onClick={(e) => {
                  const targetLink = e.target.closest('a');
                  if (targetLink) {
                    const href = targetLink.getAttribute('href');
                    if (href && href.startsWith('#')) {
                      e.preventDefault();
                      const targetId = href.substring(1);
                      const element = document.getElementById(targetId);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    } else if (href && href.startsWith('/') && !targetLink.getAttribute('target')) {
                      e.preventDefault();
                      navigate(href);
                    }
                  }
                }}
                dangerouslySetInnerHTML={{ __html: processedContent.html }}
              />

              {/* Direct callout box at bottom of text */}
              <div className="mt-8 bg-slate-900 text-white p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-l-4 border-brand-primary shadow-sm">
                <div className="space-y-1.5 max-w-lg">
                  <h3 className="font-display font-bold text-lg sm:text-xl text-white">
                    Skal vi kigge på dit projekt?
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    Kontakt os for en uforpligtende snak om dine muligheder og få en professionel vurdering.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 shrink-0">
                  <a
                    href="tel:22439147"
                    className="bg-white hover:bg-slate-100 text-slate-900 font-bold py-2.5 px-5 text-sm transition-all inline-flex items-center gap-2"
                  >
                    <i className="fa-solid fa-phone text-brand-primary" />
                    +45 22 43 91 47
                  </a>
                  <Link
                    to="/kontakt"
                    className="bg-brand-primary hover:bg-brand-primary/90 text-white font-bold py-2.5 px-5 text-sm transition-all inline-flex items-center gap-2"
                  >
                    <i className="fa-solid fa-envelope" />
                    Kontakt os
                  </Link>
                </div>
              </div>
            </div>

            {/* Gallery Section */}
            {gallery && gallery.billeder && gallery.billeder.length > 0 && (
              <div className="pt-8 border-t border-slate-100 space-y-6">
                <h2 className="font-display font-bold text-2xl text-slate-900">
                  Billeder fra vores arbejde
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {gallery.billeder.map((imgUrl, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImage(imgUrl)}
                      className="group relative h-32 sm:h-40 rounded-none overflow-hidden border border-slate-100 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-secondary"
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=300,fit=cover,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                        alt={`${service.titel?.da || service.titel} - Detalje og referencebillede ${idx + 1} fra Tømrerfirmaet RNS`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <i className="fa-solid fa-magnifying-glass-plus text-white text-lg" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar (Related Projects & Contact CTA) */}
          <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-28 lg:self-start">
            {/* All Services Navigation */}
            <div className="bg-slate-50 p-6 sm:p-8 rounded-none border-b border-slate-300 space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                <h3 className="font-display font-bold text-xl text-slate-900">
                  Vores ydelser
                </h3>
                <Link
                  to="/ydelser"
                  className="text-xs font-semibold text-brand-primary hover:underline uppercase tracking-wider"
                >
                  Se alle
                </Link>
              </div>
              <nav className="space-y-2">
                {[
                  { title: "Sommerhusrenovering", path: "/ydelser/sommerhusrenovering", slugKey: "sommerhusrenovering", icon: "fa-solid fa-house-chimney-window" },
                  { title: "Tilbygning", path: "/ydelser/tilbygning", slugKey: "tilbygning", icon: "fa-solid fa-person-digging" },
                  { title: "Ombygning", path: "/ydelser/ombygning", slugKey: "ombygning", icon: "fa-solid fa-hammer" },
                  { title: "Tømrerarbejde", path: "/ydelser/toemrer", slugKey: "toemrer", icon: "fa-solid fa-screwdriver-wrench" },
                  { title: "Maler på Djursland", path: "/ydelser/maler", slugKey: "maler", icon: "fa-solid fa-paint-roller" },
                  { title: "Udskiftning af vinduer og døre", path: "/ydelser/udskiftning-af-vinduer", slugKey: "udskiftning-af-vinduer", icon: "fa-solid fa-door-open" },
                  { title: "Nyt tag & tagrenovering", path: "/ydelser/asbestarbejde-og-tagrenovering", slugKey: "asbestarbejde-og-tagrenovering", icon: "fa-solid fa-house" }
                ].map((item) => {
                  const isCurrent = slug === item.slugKey || (item.slugKey === 'asbestarbejde-og-tagrenovering' && slug === 'tagrenovering');
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center justify-between p-3 transition-all border ${
                        isCurrent
                          ? 'bg-brand-primary text-white border-brand-primary font-bold shadow-xs'
                          : 'bg-white text-slate-700 hover:bg-slate-100 hover:text-brand-primary border-slate-200/80 font-medium'
                      }`}
                    >
                      <span className="flex items-center gap-3 text-sm">
                        <i className={`${item.icon} text-xs ${isCurrent ? 'text-white' : 'text-brand-primary'}`} />
                        {item.title}
                      </span>
                      <i className={`fa-solid fa-chevron-right text-xs ${isCurrent ? 'text-white' : 'text-slate-400'}`} />
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Related Projects */}
            {relatedProjects.length > 0 && (
              <div className="bg-slate-50 p-8 rounded-none border-b border-slate-300 space-y-6">
                <h3 className="font-display font-bold text-xl text-slate-900">
                  Udførte {service.titel.da.toLowerCase()}projekter
                </h3>
                <div className="space-y-4">
                  {relatedProjects.map((project) => (
                    <div key={project.id} className="flex gap-4 items-center bg-white p-4 rounded-none border-b border-slate-200 shadow-sm">
                      <div>
                        <span className="text-[10px] font-semibold text-black uppercase tracking-wider">
                          {project.lokation?.da}
                        </span>
                        <h4 className="font-display font-bold text-slate-800 text-sm leading-tight">
                          {project.titel.da}
                        </h4>
                        <Link
                          to="/referencer"
                          className="text-xs text-slate-500 hover:text-brand-primary underline mt-1 inline-block"
                        >
                          Se i projekter
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sticky Contact Card */}
            <div className="bg-brand-primary text-white p-8 rounded-none border-b border-white space-y-6 shadow-md">
              <h3 className="font-display font-bold text-2xl">
                Skal vi hjælpe dig med {service.titel.da.toLowerCase()}?
              </h3>
              <p className="text-white text-sm leading-relaxed">
                Vi står altid klar til at rådgive dig og give et uforpligtende tilbud på dit næste projekt.
              </p>
              <div className="space-y-3 pt-2">
                <a
                  href="tel:22439147"
                  className="w-full bg-transparent text-white font-semibold py-3 px-4 rounded-none border border-white transition-all flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-phone" />
                  +45 22 43 91 47
                </a>
                <Link
                  to="/kontakt"
                  className="w-full bg-white hover:bg-slate-100 text-black font-semibold py-3 px-4 rounded-none transition-all flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-envelope" />
                  Skriv en besked
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setActiveImage(null)}
        >
          <button
            onClick={() => setActiveImage(null)}
            className="absolute top-6 right-6 text-white hover:text-brand-secondary text-3xl cursor-pointer focus:outline-none"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <div className="max-w-5xl max-h-[85vh] overflow-hidden rounded-none" onClick={(e) => e.stopPropagation()}>
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${activeImage.replace('https://assets.auxodev.dk/', '')}`}
              alt={`${service.titel?.da || service.titel} - Forstørret referencebillede fra Tømrerfirmaet RNS`}
              className="w-full h-full object-contain max-h-[85vh] touch-pan-y"
            />
          </div>
        </div>
      )}
    </>
  );
}
